import React, { useCallback, useEffect, useMemo, useState } from "react";
import { v4 as uuidv4 } from "uuid";
import { defineMessage, defineMessages, injectIntl, IntlShape } from "react-intl";
import queryString from "query-string";

// Rest
import { useHttpClient } from "../../lib/http";
import { BACKOFFICE_MANAGER_URL } from "../witadmin/request";

// GRPC
import { useGrpcClient, usePropertyGrpcCall } from "../../lib/grpc";

// PROTO
import { PackageAPIPromiseClient } from "../../gen/witbooking/backoffice/package/v1/package_api_grpc_web_pb";
import {
  CreatePackageRequest,
  GetPackageRequest,
  GetPackagesRequest,
  Package,
  Translation as PackageTranslation,
  UpdatePackageRequest,
  DeletePackageRequest,
  ReorderPackageRequest,
  Multimedia,
  MultimediaTranslation,
} from "../../gen/witbooking/backoffice/package/v1/package_api_pb";
import schema from "../../gen/schema/witbooking.backoffice.bundle.v1/Package.json";

// Components
import { FormConfiguration } from "../shared/crud/form";
import SimpleCrud, { defaultCrudMessages } from "../shared/crud/simple-crud";
import { TableColumns } from "../shared/crud/list";
import { StringRenderer, mapRenderer, ArrayRenderer } from "../shared/renderers/table";
import { translationFieldConfig } from "../shared/widgets/translation";
import { HtmlEditorWidget } from "../shared/widgets/html";
import { smartSelectorFieldConfig, SmartSelectorItem } from "../shared/widgets/SmartSelector";
import { usePropertyLocales } from "../../components/property-locale-provider";
import { usePropertyData } from "../../components/property-admin-data-provider";
import {
  getDataByActions,
  imageManagerConfig,
  Translation,
  Image,
  ImageManagerProvider,
} from "../shared/widgets/ImageManager";
import { getLanguageFromFullLocale, useConfig } from "../../lib/config";
import { FormContainer } from "./form-style";
import { SwitchGroup } from "../shared/widgets/SwitchGroup";
import { withAuthorizationVerifier } from "../../components/authorization-verifier";
import { SECTION } from "../../lib/constants";
import { CheckboxInput } from "../shared/widgets/checkboxInput";
import SectionUnderline from "../shared/widgets/sectionUnderline";
import ExtrasMultiCheckbox from "../shared/widgets/extrasMultiCheckbox";
import PromotionsMultiCheckbox from "../shared/widgets/promotionsMultiCheckbox";
import { DatePickerWidget } from "../shared/widgets/datePicker";
import WeekDaysMultiCheckbox from "../shared/widgets/weekDaysMultiCheckbox";
import { RendererProps } from "../shared/renderers";
import { isBefore, isSameDay } from "date-fns";
import styled from "styled-components";
import Menu from "../witadmin/CRUD/EntityForm/Menu";
import media from "../../lib/media";

// Translations
const packagesMessages = defineMessages({
  entityName: {
    id: "package.entityName",
    defaultMessage: "Packages",
  },
  minStay: {
    id: "package.minStay",
    defaultMessage: "minStay",
  },
  maxStay: {
    id: "package.maxStay",
    defaultMessage: "maxStay",
  },
  minRelease: {
    id: "package.minRelease",
    defaultMessage: "minRelease",
  },
  maxRelease: {
    id: "package.maxRelease",
    defaultMessage: "maxRelease",
  },
  travelWindow: {
    id: "package.travelWindow",
    defaultMessage: "travelWindow",
  },
  extras: {
    id: "package.extras",
    defaultMessage: "extras",
  },
  dateMustBeAfter: {
    id: "modification.translation.dateMustBeAfter",
    defaultMessage: "The end date must be greater than the start date",
  },
  translationsList: { id: "package.translations", defaultMessage: "translations" },
  multimediaList: { id: "package.images", defaultMessage: "multimediaList" },
  travelWindowSection: { id: "package.travelWindow", defaultMessage: "travelWindow" },
  extrasList: { id: "package.extras", defaultMessage: "extras" },
  promotionsList: { id: "package.promotions", defaultMessage: "promotions" },
  travelWindowList: { id: "package.bookingWindow", defaultMessage: "bookingWindow" },
  inventoryLinesList: {
    id: "package.inventoryLines",
    defaultMessage: "inventoryLinesList",
  },
});

const tableHeaderMessages = defineMessages({
  ticker: { id: "package.ticker", defaultMessage: "Identifier" },
  translationsList: { id: "package.name", defaultMessage: "Name" },
  description: { id: "package.description", defaultMessage: "Description" },
  startDate: { id: "package.startDate", defaultMessage: "startDate" },
  endDate: { id: "package.endDate", defaultMessage: "endDate" },
  weekDaysList: { id: "package.weekDays", defaultMessage: "weekDays" },
});
const filterMessages = defineMessages({
  accommodation: {
    id: "app.smartSelector.filter.accommodation",
    defaultMessage: "Accommodation",
  },
  conditions: {
    id: "app.smartSelector.filter.conditions",
    defaultMessage: "Conditions",
  },
  confirmation: {
    id: "app.smartSelector.filter.confirmation",
    defaultMessage: "Condition",
  },
  mealplan: { id: "app.smartSelector.filter.mealplan", defaultMessage: "Meal Plan" },
});

const messages = {
  ...defaultCrudMessages,
  ...packagesMessages,
  tableHeaders: tableHeaderMessages,
};

const StyledMenu = styled(Menu)`
  z-index: 2;
  display: none;
  margin-right: 15px;
  ${media.up.sml`
    display: block;
  `};
`;

const PackagesContainer = styled.div`
  display: flex;
  & > div:first-child {
    margin-left: 20px;
    margin-top: 20px;
  }
`;

// Interfaces
interface CustomPackage extends Omit<Package.AsObject, "multimediaList"> {
  multimediaList: Image[];
}

interface menuField {
  displayName: string;
  menuIndex: string;
  widget: string;
}

// TODO: These methods could be generated automatically.
function translationFromObject(obj: PackageTranslation.AsObject): PackageTranslation {
  const result = new PackageTranslation();
  result.setLocale(obj.locale);
  result.setName(obj.name);
  result.setDescription(obj.description);

  return result;
}

function packageFromObject(obj: CustomPackage): Package {
  const result = new Package();
  result.setId(obj.id);
  result.setTicker(obj.ticker);
  result.setTranslationsList(obj.translationsList.map(translationFromObject));
  result.setInventoryLinesList(obj.inventoryLinesList);
  result.setActive(obj.active);
  return result;
}

function multimediaFromCustomMultimedia(multimediaList: Multimedia.AsObject[]): Image[] {
  return multimediaList.map(multimedia =>
    Object.assign(new File([""], multimedia.filename), {
      id: multimedia.id,
      url: multimedia.url,
      filename: multimedia.filename,
      translations:
        (multimedia.translationsList &&
          multimedia.translationsList.length &&
          multimedia.translationsList.reduce((accum, curr) => {
            const locale = curr.locale;
            return {
              ...accum,
              [locale]: {
                title: curr.title,
                description: curr.description,
              },
            };
          }, {})) ||
        {},
    })
  );
}

function packageFromCustomPackage({ multimediaList, ...props }: Package.AsObject): CustomPackage {
  return {
    ...props,
    multimediaList: multimediaFromCustomMultimedia(multimediaList),
  } as CustomPackage;
}

function customMultimediaTranslationsFromMultimediaTranslations(
  translations: Translation = {}
): MultimediaTranslation.AsObject[] {
  const results: MultimediaTranslation.AsObject[] = Object.keys(translations).map(
    locale =>
      ({
        locale,
        description: translations[locale].description || "",
        title: translations[locale].title,
      } as MultimediaTranslation.AsObject)
  );
  return results;
}

export function CustomDateRenderer({ value = "" }: RendererProps<string>) {
  return value ? value.substring(0, 10) : null;
}

const menuFieldsNames = [
  "translationsList",
  "multimediaList",
  "travelWindowSection",
  "extrasList",
  "promotionsList",
  "travelWindowList",
  "inventoryLinesList",
];

function PackageCrud({ propertyTicker, intl }: { propertyTicker: string; intl: IntlShape }) {
  const [packages, setPackages] = useState<CustomPackage[]>([]);
  const [menuFields, setMenuFields] = useState<menuField[]>();
  const config = useConfig();

  useEffect(() => {
    const menu: menuField[] = [];

    menuFieldsNames.forEach(name =>
      menu.push({
        displayName: intl.formatMessage(packagesMessages[name as keyof typeof packagesMessages]),
        menuIndex: `root_${name}`,
        widget: "section",
      })
    );

    setMenuFields(menu);
  }, [intl]);

  // Grpc
  const grpcClient = useGrpcClient(PackageAPIPromiseClient);
  const getPackages = usePropertyGrpcCall(grpcClient, grpcClient.getPackages);
  const getPackage = usePropertyGrpcCall(grpcClient, grpcClient.getPackage);
  const createPackage = usePropertyGrpcCall(grpcClient, grpcClient.createPackage);
  const updatePackage = usePropertyGrpcCall(grpcClient, grpcClient.updatePackage);
  const deletePackage = usePropertyGrpcCall(grpcClient, grpcClient.deletePackage);
  const reorderPackage = usePropertyGrpcCall(grpcClient, grpcClient.reorderPackage);

  // Rest
  const httpClient = useHttpClient(BACKOFFICE_MANAGER_URL);

  const allowedLocales = usePropertyLocales();
  const propertyData = usePropertyData();
  const allowedInventoryLines = useMemo((): SmartSelectorItem[] => {
    if (propertyData?.inventories?.length) {
      return propertyData.inventories.map(
        inventory =>
          ({
            name: `${inventory.accommodation.name} / ${inventory.configuration.name} , ${inventory.mealPlan.name} , ${inventory.condition.name}`,
            ticker: inventory.ticker,
            filters: {
              accommodation: inventory.accommodation,
              condition: inventory.condition,
              configuration: inventory.configuration,
              mealPlan: inventory.mealPlan,
            },
          } as SmartSelectorItem)
      );
    }
    return [];
  }, [propertyData]);
  const customSchema = useMemo(
    () => ({
      ...schema,
      properties: {
        ...schema.properties,
        multimedia: {
          ...schema.properties.multimedia,
          items: {
            ...schema.properties.multimedia.items,
            properties: {
              ...schema.properties.multimedia.items.properties,
              translations: {
                ...schema.properties.multimedia.items.properties.translations,
                items: {
                  ...schema.properties.multimedia.items.properties.translations.items,
                  required: ["title"],
                },
                type: "object",
              },
            },
            additionalProperties: true,
          },
        },
        travelWindowSection: {
          type: "string",
        },
      },
    }),
    []
  );
  const featchCreateImages = useCallback(
    async (packageId: string, data: Image[]) => {
      if (data.length) {
        const formData = new FormData();
        data.forEach(d => {
          formData.append("images", d, d.id);
          formData.append(
            "medias",
            new Blob(
              [
                JSON.stringify({
                  id: d.id,
                  filename: d.filename,
                  translations: customMultimediaTranslationsFromMultimediaTranslations(
                    d.translations
                  ),
                }),
              ],
              {
                type: "application/json",
              }
            )
          );
        });
        await httpClient.post<FormData, Multimedia.AsObject>(
          `/v1/${propertyTicker}/package/${packageId}/media/multi`,
          formData
        );
      }
    },
    [httpClient, propertyTicker]
  );

  const featchRemoveImages = useCallback(
    async (packageId: string, data: Image[]) => {
      if (data.length) {
        const request = data.map(d => d.id);
        await httpClient.delete<any>(
          `/v1/${propertyTicker}/package/${packageId}/media?${queryString.stringify({
            mediaIds: request,
          })}`
        );
      }
    },
    [httpClient, propertyTicker]
  );

  const featchUpdateImages = useCallback(
    async (packageId: string, data: Image[]) => {
      if (data.length) {
        const request = data.map(d => ({
          ...d,
          translations: customMultimediaTranslationsFromMultimediaTranslations(d.translations),
        }));
        await httpClient.put<any, Multimedia.AsObject>(
          `/v1/${propertyTicker}/package/${packageId}/media`,
          request
        );
      }
    },
    [httpClient, propertyTicker]
  );

  const featchUploadImages = useCallback(
    async ({ id, multimediaList }: CustomPackage) => {
      const currentPackage = packages.find(p => p.id === id);

      if (!currentPackage) {
        return await featchCreateImages(id, multimediaList);
      }
      const { create, update, remove } = getDataByActions(
        currentPackage.multimediaList,
        multimediaList
      );

      return await Promise.all([
        featchCreateImages(id, create),
        featchRemoveImages(id, remove),
        featchUpdateImages(id, update),
      ]);
    },
    [packages, featchRemoveImages, featchCreateImages, featchUpdateImages]
  );

  const handleGetAll = useCallback(async (): Promise<CustomPackage[]> => {
    const request = new GetPackagesRequest();
    const response = await getPackages(request);
    const result = response.getPackageList().map(e => packageFromCustomPackage(e.toObject()));
    setPackages(result);
    return result;
  }, [getPackages, setPackages]);

  const handleGetOne = useCallback(
    async (id: string) => {
      const request = new GetPackageRequest();
      request.setPackageId(id);
      const response = await getPackage(request);
      const result = packageFromCustomPackage(response.getPackage()?.toObject()!!);
      setPackages([result]);
      return result;
    },
    [getPackage, setPackages]
  );

  const handleCreate = useCallback(
    async (entity: CustomPackage) => {
      const request = new CreatePackageRequest();
      request.setPackage(packageFromObject(entity));
      const [respose] = await Promise.all([createPackage(request), featchUploadImages(entity)]);
      return respose.getPackage()?.getId()!!;
    },
    [createPackage, featchUploadImages]
  );

  const handleUpdate = useCallback(
    async (entity: CustomPackage) => {
      const request = new UpdatePackageRequest();
      request.setPackage(packageFromObject(entity));
      await Promise.all([updatePackage(request), featchUploadImages(entity)]);
    },
    [updatePackage, featchUploadImages]
  );

  const handleDelete = useCallback(
    async (entityId: string) => {
      const request = new DeletePackageRequest();
      request.setPackageId(entityId);
      await deletePackage(request);
    },
    [deletePackage]
  );

  const handleReorder = useCallback(
    async (id: string, after?: string) => {
      const request = new ReorderPackageRequest();
      request.setPackageId(id);
      request.setAfterId(after || "");

      await reorderPackage(request);
    },
    [reorderPackage]
  );

  const travelWindowsFormConfiguration = useMemo(() => {
    return {
      __fieldOrder: ["*"],
      __useProtoStyleArrays: true,
      id: {
        hidden: true,
      },
      startDate: {
        fieldWidget: DatePickerWidget,
        messages: {
          label: defineMessage({ id: "package.startDate", defaultMessage: "startDate" }),
        },
      },
      endDate: {
        fieldWidget: DatePickerWidget,
        messages: {
          label: defineMessage({ id: "package.endDate", defaultMessage: "endDate" }),
        },
      },
      weekDaysList: {
        fieldWidget: WeekDaysMultiCheckbox,
        messages: {
          label: defineMessage({ id: "package.weekDays", defaultMessage: "weekDays" }),
        },
      },
    };
  }, []);

  const travelWindowsTableColumns = useMemo(() => {
    return {
      startDate: {
        renderer: CustomDateRenderer,
      },
      endDate: {
        renderer: CustomDateRenderer,
      },
      weekDaysList: {
        renderer: ArrayRenderer,
      },
    };
  }, []);

  const formConfiguration = useMemo<
    FormConfiguration<CustomPackage & { travelWindowSection?: string }>
  >(() => {
    return {
      __fieldOrder: [
        "ticker",
        "active",
        "translationsList",
        "multimediaList",
        "travelWindowSection",
        "minStay",
        "maxStay",
        "minRelease",
        "maxRelease",
        "extrasList",
        "promotionsList",
        "travelWindowsList",
        "*",
      ],
      __useProtoStyleArrays: true,
      id: {
        default: () => uuidv4(),
        hidden: true,
      },
      ticker: {
        readonly: isNew => !isNew,
        messages: {
          label: defineMessage({ id: "package.ticker", defaultMessage: "Identifier" }),
        },
      },
      active: {
        messages: {
          label: defineMessage({ id: "package.active", defaultMessage: "active" }),
        },
        default: () => true,
        widget: SwitchGroup,
      },
      translationsList: {
        ...translationFieldConfig(allowedLocales || []),
        children: {
          __fieldOrder: ["name", "description", "*"],
          name: {
            messages: {
              label: defineMessage({ id: "package.translation.name", defaultMessage: "Name" }),
            },
          },
          description: {
            widget: HtmlEditorWidget,
            messages: {
              label: defineMessage({
                id: "package.translation.description",
                defaultMessage: "Description",
              }),
            },
          },
          ...translationFieldConfig(allowedLocales || []).children,
        },
        messages: {
          label: defineMessage({ id: "package.translations", defaultMessage: "Translations" }),
        },
      },
      inventoryLinesList: {
        ...smartSelectorFieldConfig(allowedInventoryLines, {
          filterNames: [
            intl.formatMessage(filterMessages.accommodation),
            intl.formatMessage(filterMessages.conditions),
            intl.formatMessage(filterMessages.confirmation),
            intl.formatMessage(filterMessages.mealplan),
          ],
        }),
        isCustomField: true,
        messages: {
          label: defineMessage({ id: "package.inventoryLines", defaultMessage: "Inventory lines" }),
          description: defineMessage({
            id: "package.inventoryLines.description",
            defaultMessage: "This package will only apply to the selected inventory lines.",
          }),
        },
      },
      multimediaList: {
        ...imageManagerConfig({
          locales: allowedLocales,
          maxSizeMB: config.mediaMaxSizeMB,
          maxFiles: config.mediaMaxFiles,
          minWidth: config.mediaMinWidth,
        }),
        messages: {
          label: defineMessage({ id: "package.images", defaultMessage: "Images management" }),
        },
      },
      travelWindowSection: {
        fieldWidget: SectionUnderline,
        messages: {
          label: defineMessage({
            id: "package.travelWindow",
            defaultMessage: "travelWindow",
          }),
        },
      },
      minStay: {
        messages: {
          label: defineMessage({ id: "package.minStay", defaultMessage: "minStay" }),
        },
        widget: CheckboxInput,
      },
      maxStay: {
        messages: {
          label: defineMessage({ id: "package.maxStay", defaultMessage: "maxStay" }),
        },
        widget: CheckboxInput,
      },
      minRelease: {
        messages: {
          label: defineMessage({ id: "package.minRelease", defaultMessage: "minRelease" }),
        },
        widget: CheckboxInput,
      },
      maxRelease: {
        messages: {
          label: defineMessage({ id: "package.maxRelease", defaultMessage: "maxRelease" }),
        },
        widget: CheckboxInput,
      },
      extrasList: {
        messages: {
          label: defineMessage({ id: "package.extras", defaultMessage: "extras" }),
        },
        fieldWidget: ExtrasMultiCheckbox,
      },
      promotionsList: {
        messages: {
          label: defineMessage({ id: "package.promotions", defaultMessage: "promotions" }),
        },
        fieldWidget: PromotionsMultiCheckbox,
      },
      travelWindowsList: {
        id: "root_travelWindowList",
        nestedFormConfiguration: travelWindowsFormConfiguration,
        nestedTableColumns: travelWindowsTableColumns,
        nestedEntityName: {
          defaultMessage: "bookingWindow",
          id: "package.bookingWindow",
        },
      },
    };
  }, [
    allowedLocales,
    allowedInventoryLines,
    intl,
    config.mediaMaxFiles,
    config.mediaMaxSizeMB,
    config.mediaMinWidth,
    travelWindowsFormConfiguration,
    travelWindowsTableColumns,
  ]);

  const tableColumns = useMemo<TableColumns<CustomPackage>>(() => {
    return {
      ticker: {
        renderer: StringRenderer,
      },
      translationsList: {
        children: {
          name: {
            renderer: mapRenderer(StringRenderer, (v, intl) => {
              if (!v || !v.length) return undefined;
              const current = v.find(t => t.locale === getLanguageFromFullLocale(intl.locale));
              return current?.name || v[0].name;
            }),
          },
          description: {
            renderer: mapRenderer(
              StringRenderer,
              (v, intl) => {
                if (!v || !v.length) return undefined;
                const current = v.find(t => t.locale === getLanguageFromFullLocale(intl.locale));
                return current?.description || v[0].description;
              },
              { isHtml: true, maxCharacters: 200 }
            ),
          },
        },
      },
    };
  }, []);

  const validate = useCallback(
    (formData, errors) => {
      if (formData?.startDate && formData.endDate) {
        const startDate = new Date(formData.startDate);
        const endDate = new Date(formData.endDate);

        if (isBefore(endDate, startDate) || isSameDay(endDate, startDate)) {
          errors.endDate.addError(intl.formatMessage(messages.dateMustBeAfter));
        }
      }
      return errors;
    },
    [intl]
  );

  return (
    <PackagesContainer>
      <ImageManagerProvider>
        <StyledMenu fields={menuFields} showMenu={!!menuFields} className={""} />
        <SimpleCrud<CustomPackage & { travelWindowSection?: string }>
          formContainer={FormContainer}
          messages={messages}
          onGetAll={handleGetAll}
          onGetOne={handleGetOne}
          onCreate={handleCreate}
          onUpdate={handleUpdate}
          onDelete={handleDelete}
          onReorder={handleReorder}
          formConfiguration={formConfiguration}
          schema={customSchema}
          tableColumns={tableColumns}
          section={SECTION.PACKAGES}
          validate={validate}
        />
      </ImageManagerProvider>
    </PackagesContainer>
  );
}
export default injectIntl(withAuthorizationVerifier(PackageCrud));
